import config from "../../config.json";

const BASE_AUTH_API = config.BASE_AUTH_API;

// Errors carry the backend's `code` (e.g. MENU_NAME_TAKEN, ADDITION_IN_USE)
// so screens can explain what happened.
export class MenuApiError extends Error {
  constructor(message, { status, code, details } = {}) {
    super(message);
    this.name = "MenuApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export async function menuRequest(path, { method = "GET", body, form } = {}) {
  const token = localStorage.getItem("auth-token");
  const response = await fetch(`${BASE_AUTH_API}${path}`, {
    method,
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(form ? { body: form } : body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new MenuApiError(data.message || data.error || "Request failed", { status: response.status, code: data.code, details: data.details });
  }
  return data;
}

const query = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "" && value !== false) search.append(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

// ------------------------------------------------------------------ menus
export const fetchMenusAPI = async (params = {}) => (await menuRequest(`menu/menus${query(params)}`)).menus || [];
export const fetchMenuAPI = async (menuId, params = {}) => (await menuRequest(`menu/menus/${menuId}${query(params)}`)).menu;
export const createMenuAPI = (data) => menuRequest("menu/menus", { method: "POST", body: data });
export const importMenuDraftAPI = (data) => menuRequest("menu/menus/import", { method: "POST", body: data });
export const updateMenuPropsAPI = (menuId, data) => menuRequest(`menu/menus/${menuId}`, { method: "PATCH", body: data });
export const duplicateMenuAPI = (menuId, data = {}) => menuRequest(`menu/menus/${menuId}/duplicate`, { method: "POST", body: data });
export const activateMenuAPI = (menuId) => menuRequest(`menu/menus/${menuId}/activate`, { method: "POST" });
export const archiveMenuAPI = (menuId) => menuRequest(`menu/menus/${menuId}/archive`, { method: "POST" });
// Same menu record back, as a draft.
export const restoreMenuAPI = (menuId) => menuRequest(`menu/menus/${menuId}/restore`, { method: "POST" });
export const importIntoMenuAPI = (menuId, data) => menuRequest(`menu/menus/${menuId}/import`, { method: "POST", body: data });

// --------------------------------------------------------------- branches
export const assignMenuBranchAPI = (menuId, branchId) => menuRequest(`menu/menus/${menuId}/branches/${branchId}`, { method: "PUT" });
export const unassignMenuBranchAPI = (menuId, branchId) => menuRequest(`menu/menus/${menuId}/branches/${branchId}`, { method: "DELETE" });

// ---------------------------------------------------------------- sections
export const createSectionAPI = (menuId, data) => menuRequest(`menu/menus/${menuId}/sections`, { method: "POST", body: data });
export const updateSectionAPI = (menuId, sectionId, data) => menuRequest(`menu/menus/${menuId}/sections/${sectionId}`, { method: "PATCH", body: data });
export const archiveSectionAPI = (menuId, sectionId) => menuRequest(`menu/menus/${menuId}/sections/${sectionId}`, { method: "DELETE" });
export const restoreSectionAPI = (menuId, sectionId) => menuRequest(`menu/menus/${menuId}/sections/${sectionId}/restore`, { method: "POST" });
export const reorderSectionsAPI = (menuId, sectionIds) => menuRequest(`menu/menus/${menuId}/sections/order`, { method: "PUT", body: { sectionIds } });
export const reorderItemsAPI = (menuId, sectionId, itemIds) => menuRequest(`menu/menus/${menuId}/sections/${sectionId}/items/order`, { method: "PUT", body: { itemIds } });

// ------------------------------------------------------------------- items
export const createItemAPI = (menuId, sectionId, data) => menuRequest(`menu/menus/${menuId}/sections/${sectionId}/items`, { method: "POST", body: data });
// `sectionId` (optional) makes the backend also check the item is in that section.
export const updateItemAPI = (menuId, itemId, data, sectionId) => menuRequest(`menu/menus/${menuId}/items/${itemId}${query({ sectionId })}`, { method: "PATCH", body: data });
export const archiveItemAPI = (menuId, itemId, sectionId) => menuRequest(`menu/menus/${menuId}/items/${itemId}${query({ sectionId })}`, { method: "DELETE" });
export const restoreItemAPI = (menuId, itemId) => menuRequest(`menu/menus/${menuId}/items/${itemId}/restore`, { method: "POST" });
export const moveItemAPI = (menuId, itemId, sectionId) => menuRequest(`menu/menus/${menuId}/items/${itemId}/move`, { method: "POST", body: { sectionId } });
export const removeItemImageAPI = (menuId, itemId) => menuRequest(`menu/menus/${menuId}/items/${itemId}/image`, { method: "DELETE" });
export const setItemHiddenAPI = (menuId, itemId, hidden) => menuRequest(`menu/menus/${menuId}/items/${itemId}/visibility`, { method: "PATCH", body: { hidden } });
export const setItemBranchVisibilityAPI = (menuId, itemId, branchId, visible) => menuRequest(`menu/menus/${menuId}/items/${itemId}/branches/${branchId}/visibility`, { method: "PATCH", body: { visible } });
export const uploadItemImageAPI = (menuId, itemId, image) => {
  const form = new FormData();
  form.append("image", image);
  return menuRequest(`menu/menus/${menuId}/items/${itemId}/image`, { method: "PUT", form });
};

// --------------------- top-level copy (same or another authorized restaurant)
// Restaurants the current user may copy from/into (decided by the backend).
export const fetchCopyRestaurantsAPI = async () => (await menuRequest("menu/copy/restaurants")).restaurants || [];
// Dry run: { type: "item"|"section", source, destination, additionMap? } ->
// addition links (kept / mapped / skipped), dropped branch overrides, counts.
export const previewCopyAPI = async (data) => (await menuRequest("menu/copy/preview", { method: "POST", body: data })).preview;
// { source: { restaurantId, menuId, sectionId, itemId }, destination: { restaurantId, menuId, sectionId }, additionMap? }
export const copyItemAPI = (data) => menuRequest("menu/copy/item", { method: "POST", body: data });
// { source: { restaurantId, menuId, sectionId }, destination: { restaurantId, menuId }, name?, additionMap? }
export const copySectionAPI = (data) => menuRequest("menu/copy/section", { method: "POST", body: data });

// ---------------------------------------------------------- bulk discounts
// { operation, scope, sectionIds, itemIds, pricingNames, discount,
//   matchDiscount, overwrite, source, expectedVersion }
export const previewDiscountsAPI = async (menuId, data) => (await menuRequest(`menu/menus/${menuId}/discounts/preview`, { method: "POST", body: data })).preview;
export const applyDiscountsAPI = (menuId, data) => menuRequest(`menu/menus/${menuId}/discounts/apply`, { method: "POST", body: data });

// ------------------------------------------------------------ copy/history
export const copyMenuContentAPI = async (data) => (await menuRequest("menu/copy", { method: "POST", body: data })).results || [];
export const fetchMenuHistoryAPI = async (menuId, itemId) => (await menuRequest(`menu/menus/${menuId}/history${query({ itemId })}`)).history || [];
export const fetchRestaurantMenuHistoryAPI = async (restaurant) => (await menuRequest(`menu/history${query({ restaurant })}`)).history || [];
// Paged variants (MENU_HISTORY_PAGINATION): newest first, `limit` entries after the cursor (omitted = newest page). The cursor is the last entry's
// createdAt (`before`) and _id (`beforeId`); the backend breaks createdAt ties by _id, so both are needed to not skip entries.
export const fetchMenuHistoryPageAPI = async (menuId, { itemId, limit, before, beforeId } = {}) => (await menuRequest(`menu/menus/${menuId}/history${query({ itemId, limit, before, beforeId })}`)).history || [];
export const fetchRestaurantMenuHistoryPageAPI = async (restaurant, { limit, before, beforeId } = {}) => (await menuRequest(`menu/history${query({ restaurant, limit, before, beforeId })}`)).history || [];

// --------------------------------------------------------------- additions
export const fetchMenuAdditionsAPI = async (restaurant, params = {}) => (await menuRequest(`menu-additions${query({ restaurant, ...params })}`)).additions || [];
export const createMenuAdditionAPI = (data) => menuRequest("menu-additions", { method: "POST", body: data });
export const updateMenuAdditionAPI = (additionId, data) => menuRequest(`menu-additions/${additionId}`, { method: "PUT", body: data });
export const archiveMenuAdditionAPI = (additionId) => menuRequest(`menu-additions/${additionId}`, { method: "DELETE" });
export const restoreMenuAdditionAPI = (additionId) => menuRequest(`menu-additions/${additionId}/restore`, { method: "POST" });
export const fetchMenuAdditionUsagesAPI = async (additionId, restaurant) => (await menuRequest(`menu-additions/${additionId}/usages${query({ restaurant })}`)).usages || [];
export const copyAdditionAPI = (data) => menuRequest("menu-additions/copy", { method: "POST", body: data });
export const uploadMenuAdditionsAPI = (data) => menuRequest("menu-additions/upload", { method: "POST", body: data });
export const reorderMenuAdditionsAPI = (restaurant, additionIds) => menuRequest("menu-additions/order", { method: "PUT", body: { restaurant, additionIds } });
